// src/utils/emailValidation.jsx

import React, { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom Email Validation System
 * No external APIs required - completely self-contained
 */

// =============================================
// EMAIL SYNTAX VALIDATION
// =============================================

export function validateEmailSyntax(email) {
  const emailRegex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  if (!email || typeof email !== 'string') {
    return { valid: false, reason: 'Email is required' };
  }

  const normalizedEmail = email.toLowerCase().trim();

  if (!emailRegex.test(normalizedEmail)) {
    return { valid: false, reason: 'Invalid email format' };
  }

  return { valid: true, email: normalizedEmail };
}

// =============================================
// DISPOSABLE EMAIL DETECTION
// =============================================

const DISPOSABLE_DOMAINS = new Set([
  'tempmail.com',
  '10minutemail.com',
  'guerrillamail.com',
  'mailinator.com',
  'yopmail.com',
  'throwawaymail.com',
  'dispostable.com',
  'getnada.com',
  'mailnator.com',
  'temp-mail.org',
  'tempemail.net',
  'trashmail.com',
  'spamgourmet.com',
  'spambox.us',
  'spamspot.com',
  'fakeinbox.com',
  'fakemailgenerator.com',
  'fakemail.net',
  '1secmail.com',
  '1tempmail.com',
]);

const COMMON_TYPOS = {
  'gmail.com': ['gmail.co', 'gmial.com', 'gamil.com', 'gmai.com', 'gmail.con'],
  'yahoo.com': ['yahoo.co', 'yaho.com', 'yahho.com', 'yhaoo.com', 'yahoo.con'],
  'hotmail.com': ['hotmail.co', 'hotmail.con', 'hotmal.com', 'hotmai.com'],
  'outlook.com': ['outlook.co', 'outlok.com', 'outllok.com', 'outlook.con'],
  'icloud.com': ['icloud.co', 'iclud.com', 'iclod.com', 'icloud.con'],
  'protonmail.com': ['protonmail.co', 'protonmail.con', 'protomail.com'],
};

// =============================================
// ROLE-BASED EMAIL DETECTION
// =============================================

const ROLE_BASED_PATTERNS = [
  /^admin/i,
  /^webmaster/i,
  /^postmaster/i,
  /^hostmaster/i,
  /^support/i,
  /^sales/i,
  /^info/i,
  /^contact/i,
  /^hello/i,
  /^team/i,
  /^careers/i,
  /^jobs/i,
  /^hr/i,
  /^billing/i,
  /^accounts/i,
  /^mailer/i,
  /^no-?reply/i,
  /^donotreply/i,
  /^noreply/i,
  /^newsletter/i,
  /^marketing/i,
  /^feedback/i,
];

export function isRoleBasedEmail(localPart) {
  for (const pattern of ROLE_BASED_PATTERNS) {
    if (pattern.test(localPart)) return true;
  }
  return false;
}

// =============================================
// TYPOS DETECTION
// =============================================

export function getEmailSuggestion(email) {
  const [localPart, domain] = email.toLowerCase().split('@');
  if (!domain) return null;

  for (const [correctDomain, typos] of Object.entries(COMMON_TYPOS)) {
    if (typos.includes(domain)) {
      return `${localPart}@${correctDomain}`;
    }
  }

  return null;
}

// =============================================
// QUALITY SCORING
// =============================================

export function calculateEmailQualityScore(email, isDisposable, isRoleBased) {
  let score = 100;
  if (isDisposable) score -= 40;
  if (isRoleBased) score -= 25;

  const [localPart] = email.split('@');
  if (/^\d+$/.test(localPart)) score -= 15;
  if (localPart.length < 5) score -= 10;
  if (localPart.includes('test') || localPart.includes('demo')) score -= 20;
  if (localPart.includes('temp') || localPart.includes('fake')) score -= 30;

  return Math.max(0, Math.min(100, score));
}

// =============================================
// COMPREHENSIVE EMAIL VALIDATION
// =============================================

export async function validateEmail(email, options = {}) {
  const {
    checkDisposable = true,
    checkRoleBased = true,
    generateSuggestions = true,
    minQualityScore = 0,
  } = options;

  const syntaxCheck = validateEmailSyntax(email);
  if (!syntaxCheck.valid) {
    return {
      valid: false,
      reason: syntaxCheck.reason,
      score: 0,
      isDisposable: false,
      isRoleBased: false,
      suggestions: null,
    };
  }

  const normalizedEmail = syntaxCheck.email;
  const [localPart, domain] = normalizedEmail.split('@');

  let isDisposable = false;
  if (checkDisposable) {
    isDisposable = DISPOSABLE_DOMAINS.has(domain);
  }

  let isRoleBased = false;
  if (checkRoleBased) {
    isRoleBased = isRoleBasedEmail(localPart);
  }

  let suggestions = null;
  if (generateSuggestions) {
    suggestions = getEmailSuggestion(normalizedEmail);
  }

  const score = calculateEmailQualityScore(
    normalizedEmail,
    isDisposable,
    isRoleBased,
  );

  let valid = true;
  let reason = null;

  if (isDisposable) {
    valid = false;
    reason = 'Disposable email addresses are not allowed';
  } else if (isRoleBased) {
    valid = false;
    reason =
      'Role-based emails are not allowed. Please use a personal email address';
  } else if (score < minQualityScore) {
    valid = false;
    reason = `Email quality score (${score}) is below minimum requirement (${minQualityScore})`;
  }

  return {
    valid,
    reason,
    score,
    email: normalizedEmail,
    localPart,
    domain,
    isDisposable,
    isRoleBased,
    suggestions:
      suggestions && suggestions !== normalizedEmail ? suggestions : null,
    quality: {
      score,
      isHighQuality: score >= 70,
      isMediumQuality: score >= 40 && score < 70,
      isLowQuality: score < 40,
    },
  };
}

// =============================================
// REACT HOOK FOR EMAIL VALIDATION
// =============================================

export function useEmailValidation(options = {}) {
  const {
    debounceMs = 500,
    checkDisposable = true,
    checkRoleBased = true,
    generateSuggestions = true,
    minQualityScore = 0,
    onValidChange = null,
  } = options;

  const [emailValue, setEmailValue] = useState('');
  const [validation, setValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const debounceTimeout = useRef(null);

  const validate = useCallback(
    async emailToValidate => {
      if (!emailToValidate || emailToValidate.trim() === '') {
        setValidation(null);
        return null;
      }

      setIsValidating(true);
      try {
        const result = await validateEmail(emailToValidate, {
          checkDisposable,
          checkRoleBased,
          generateSuggestions,
          minQualityScore,
        });
        setValidation(result);
        if (onValidChange) onValidChange(result.valid, result);
        return result;
      } finally {
        setIsValidating(false);
      }
    },
    [
      checkDisposable,
      checkRoleBased,
      generateSuggestions,
      minQualityScore,
      onValidChange,
    ],
  );

  const handleEmailChange = useCallback(
    newEmail => {
      setEmailValue(newEmail);
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(
        () => validate(newEmail),
        debounceMs,
      );
    },
    [validate, debounceMs],
  );

  const handleEmailBlur = useCallback(() => {
    setIsTouched(true);
    if (emailValue) validate(emailValue);
  }, [emailValue, validate]);

  const resetValidation = useCallback(() => {
    setEmailValue('');
    setValidation(null);
    setIsTouched(false);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, []);

  return {
    email: emailValue,
    setEmail: handleEmailChange,
    validation,
    isValidating,
    touched: isTouched,
    handleBlur: handleEmailBlur,
    reset: resetValidation,
    isValid: validation?.valid || false,
    errorMessage:
      isTouched && validation && !validation.valid ? validation.reason : null,
    suggestion: validation?.suggestions,
    qualityScore: validation?.score || 0,
    quality: validation?.quality || null,
  };
}

// =============================================
// EMAIL VALIDATION COMPONENT
// =============================================

export function EmailValidationInput({
  value,
  onChange,
  onBlur,
  placeholder = 'you@example.com',
  required = true,
  className = '',
  onValidationChange,
  ...props
}) {
  const {
    validation,
    isValidating,
    touched,
    errorMessage,
    suggestion,
    qualityScore,
    quality,
    handleBlur: hookHandleBlur,
    setEmail,
  } = useEmailValidation({
    checkDisposable: true,
    checkRoleBased: true,
    generateSuggestions: true,
    minQualityScore: 30,
    onValidChange: onValidationChange,
  });

  // Sync with parent value
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setEmail(value);
    }
  }, [value, setEmail]);

  const handleInputChange = e => {
    const newValue = e.target.value;
    setEmail(newValue);
    if (onChange) onChange(e);
  };

  const handleInputBlur = e => {
    hookHandleBlur();
    if (onBlur) onBlur(e);
  };

  const getBorderColor = () => {
    if (!touched) return 'var(--border)';
    if (validation?.valid) return '#10b981';
    if (errorMessage) return '#ef4444';
    return 'var(--border)';
  };

  const getQualityColor = () => {
    if (!quality) return '#6b7280';
    if (quality.isHighQuality) return '#10b981';
    if (quality.isMediumQuality) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="relative">
        <input
          type="email"
          value={value}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="w-full px-4 py-2 rounded-xl outline-none transition-all"
          style={{
            backgroundColor: 'transparent',
            border: `1px solid ${getBorderColor()}`,
            color: 'var(--text)',
          }}
          {...props}
        />

        {isValidating && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div
              className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--text)' }}
            />
          </div>
        )}

        {!isValidating && touched && validation?.valid && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <svg
              className="w-5 h-5 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}
      </div>

      {touched && validation && (
        <div className="mt-1">
          <div className="flex justify-between text-xs mb-0.5">
            <span style={{ color: 'var(--muted)' }}>Email Quality</span>
            <span style={{ color: getQualityColor() }}>{qualityScore}%</span>
          </div>
          <div
            className="h-1 rounded-full overflow-hidden"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${qualityScore}%`,
                backgroundColor: getQualityColor(),
              }}
            />
          </div>
        </div>
      )}

      {touched && errorMessage && (
        <div className="flex items-center gap-1 mt-1">
          <svg
            className="w-3 h-3 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-xs text-red-500">{errorMessage}</span>
        </div>
      )}

      {touched && !validation?.valid && suggestion && (
        <div className="flex items-center gap-1 mt-1">
          <svg
            className="w-3 h-3 text-yellow-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-xs" style={{ color: 'var(--muted)' }}>
            Did you mean:{' '}
            <button
              type="button"
              onClick={() => {
                const syntheticEvent = { target: { value: suggestion } };
                handleInputChange(syntheticEvent);
              }}
              className="underline hover:opacity-70"
              style={{ color: 'var(--text)' }}
            >
              {suggestion}
            </button>
          </span>
        </div>
      )}

      {touched && validation?.valid && quality && (
        <div className="flex items-center gap-2 mt-1">
          {quality.isHighQuality && (
            <span className="text-xs flex items-center gap-1 text-green-500">
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              High quality email
            </span>
          )}
          {quality.isMediumQuality && (
            <span className="text-xs flex items-center gap-1 text-yellow-500">
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Medium quality
            </span>
          )}
          {quality.isLowQuality && (
            <span className="text-xs flex items-center gap-1 text-orange-500">
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Low quality - may not be deliverable
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default {
  validateEmailSyntax,
  isRoleBasedEmail,
  getEmailSuggestion,
  validateEmail,
  useEmailValidation,
  EmailValidationInput,
};
